/* eslint-disable max-len */
import React, { useContext, useEffect, useState } from 'react';
import gql from 'graphql-tag';
import * as qs from 'query-string';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { defineMessages, useIntl } from 'react-intl';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Loading } from '../../components/Loading';
import { growlActionSuccess, growlErrors } from '../../utils/NotificationUtil';
import { NotificationContext } from '../../components/GrowlNotifications';
import { SubscriptionStatus, DEFAULT_EMPTY_CONTENT, NumberOfEmployees, PayedBy, SUCCESS } from '../../shared/Constants';
import {
  buildSubscriptionPeriodText,
  getBusinessCenterAccountStatusDisplayName,
  getFormattedDate,
  getNumberOfEmployeesLabel,
} from '../../utils/AppUtil';
import SubscriptionActionButtons from './SubscriptionActionButtons';
import Pill from '../../components/Pill';

const MANAGE_ACCOUNTS_CONFIG_QUERY = gql`
  query manageAccountsConfigQuery($accountId: ID!) {
    account(id: $accountId) {
      managedAccounts {
        id
        displayName
        type
        status
        subscriptionInformation {
          payedBy
          currentSubscription {
            purchasedOn
            endsOn
            stoppedOn
            numberOfEmployees
            duration
            payedAmount
          }
        }
        parent {
          id
          displayName
          type
        }
      }
    }
  }
`;

const RENEW_SUBSCRIPTION_MUTATION = gql`
  mutation renewSubscriptionMutation($managingAccountId: ID!, $clientAccountId: ID!) {
    renewSubscription(managingAccountId: $managingAccountId, clientAccountId: $clientAccountId)
  }
`;

const i18nMessages = defineMessages({
  success: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  soldBy: {
    id: 'account.subscription.sold-by',
    defaultMessage: 'Sold by:',
  },
  managedBy: {
    id: 'account.subscription.managed-by',
    defaultMessage: 'Managed by:',
  },
  paidBy: {
    id: 'account.subscription.paid-by',
    defaultMessage: 'Paid by:',
  },
  id: {
    id: 'account.subscription.id',
    defaultMessage: 'ID:',
  },
  noLimit: {
    id: 'account.subscription.no-limit',
    defaultMessage: 'NoLimit:',
  },
  startDate: {
    id: 'account.subscription.start-date',
    defaultMessage: 'Start date:',
  },
  status: {
    id: 'account.subscription.status',
    defaultMessage: 'Status:',
  },
  expirationRenewal: {
    id: 'account.subscription.expiration-renewal',
    defaultMessage: 'Expiration/renewal:',
  },
  employees: {
    id: 'account.subscription.employees',
    defaultMessage: 'employees',
  },
  month: {
    id: 'account.subscription.month',
    defaultMessage: 'month',
  },
  months: {
    id: 'account.subscription.months',
    defaultMessage: 'months',
  },
  inAdvance: {
    id: 'account.subscription.in-advance',
    defaultMessage: 'in advance',
  },
  active: {
    id: 'business-center-status.active',
    defaultMessage: 'Active',
  },
  stopped: {
    id: 'business-center-status.stopped',
    defaultMessage: 'Stopped',
  },
  awaitingSubscription: {
    id: 'business-center-status.awaiting-subscription',
    defaultMessage: 'Awaiting Subscription',
  },
  close: {
    id: 'actions.close',
    defaultMessage: 'Close',
  },
  per: {
    id: 'account.subscription.per',
    defaultMessage: 'per',
  },
});

const DEFAULT_ACCOUNT_STATE = {
  id: null,
  displayName: null,
  status: SubscriptionStatus.ACTIVE,
  subscriptionInformation: {
    payedBy: PayedBy.PAYED_BY_PARENT,
    currentSubscription: {
      purchasedOn: null,
      endsOn: null,
      stoppedOn: null,
      numberOfEmployees: NumberOfEmployees._0_9,
      duration: 1,
      payedAmount: 0,
    },
  },
  parent: {
    id: null,
    displayName: null,
  },
};
const ManagedAccountDetail = () => {
  const { formatMessage, formatDate } = useIntl();
  const growl = useContext(NotificationContext);
  const { aid: managingAccountId } = useParams();
  const { search } = useLocation();
  const { clientId, creditCardChange } = qs.parse(search);
  const history = useHistory();

  const [clientAccount, setClientAccount] = useState(DEFAULT_ACCOUNT_STATE);

  const [renewSubscriptionMutation] = useMutation(RENEW_SUBSCRIPTION_MUTATION, {
    refetchQueries: ['buyOrReactivateConfigQuery'],
  });
  const { loading, error, data } = useQuery(MANAGE_ACCOUNTS_CONFIG_QUERY, {
    variables: {
      accountId: managingAccountId,
    },
    fetchPolicy: 'no-cache',
  });

  const { account: accountFromQuery } = data;

  useEffect(() => {
    if (accountFromQuery) {
      setClientAccount(accountFromQuery.managedAccounts.filter((a) => a.id === clientId)[0]);
    }
    // eslint-disable-next-line
  }, [accountFromQuery]);

  useEffect(() => {
    if (creditCardChange === SUCCESS) {
      growlActionSuccess(growl, formatMessage(i18nMessages.success));
    }
    // eslint-disable-next-line
  }, [creditCardChange]);

  if (loading || clientAccount === DEFAULT_ACCOUNT_STATE) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const {
    id,
    displayName,
    status: accountStatus,
    subscriptionInformation: { payedBy, currentSubscription },
    parent: { displayName: parentDisplayName },
  } = clientAccount;

  const displayNameOfSubscriptionBuyer = payedBy === PayedBy.PAYED_BY_PARENT ? parentDisplayName : displayName;

  const purchasedOnText =
    currentSubscription && currentSubscription.purchasedOn
      ? getFormattedDate(currentSubscription.purchasedOn, formatDate)
      : DEFAULT_EMPTY_CONTENT;

  const endsOnText =
    currentSubscription && currentSubscription.endsOn
      ? getFormattedDate(currentSubscription.endsOn, formatDate)
      : DEFAULT_EMPTY_CONTENT;

  const buildNumberOfEmployeesText = () => {
    if (currentSubscription && currentSubscription.numberOfEmployees) {
      const numberOfEmployeesIncluded = getNumberOfEmployeesLabel(currentSubscription.numberOfEmployees);

      return `${numberOfEmployeesIncluded} ${formatMessage(i18nMessages.employees)}`;
    }

    return DEFAULT_EMPTY_CONTENT;
  };

  const numberOfEmployeesText = buildNumberOfEmployeesText();

  const buildSubscriptionDurationText = () => {
    if (currentSubscription && currentSubscription.duration) {
      return `${buildSubscriptionPeriodText(
        formatMessage(i18nMessages.months),
        formatMessage(i18nMessages.month),
        currentSubscription.duration,
      )}`;
    }

    return DEFAULT_EMPTY_CONTENT;
  };

  const subscriptionDurationText = buildSubscriptionDurationText();

  const statusDisplayName = getBusinessCenterAccountStatusDisplayName(
    accountStatus,
    formatMessage(i18nMessages.active),
    formatMessage(i18nMessages.stopped),
    formatMessage(i18nMessages.awaitingSubscription),
  );

  const statusText =
    accountStatus === SubscriptionStatus.PAUSED
      ? `${statusDisplayName} ${formatMessage(i18nMessages.per)} ${getFormattedDate(
          currentSubscription.stoppedOn,
          formatDate,
        )}`
      : statusDisplayName;

  const payedAmountText =
    currentSubscription && currentSubscription.payedAmount
      ? `$${currentSubscription.payedAmount.toLocaleString()}`
      : DEFAULT_EMPTY_CONTENT;

  const shouldDisplayNoLimitData =
    currentSubscription &&
    currentSubscription.numberOfEmployees &&
    currentSubscription.duration &&
    currentSubscription.payedAmount;

  const subscriptionText = `${numberOfEmployeesText}, ${payedAmountText}, ${subscriptionDurationText}`;

  const renewSubscription = async () => {
    try {
      await renewSubscriptionMutation({
        variables: {
          managingAccountId: managingAccountId,
          clientAccountId: clientAccount.id,
        },
      });

      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-sm-12 p-md-6">
            <Panel header={displayName}>
              <div className="existing-subscription-info">
                <div>
                  <strong>{formatMessage(i18nMessages.soldBy)}</strong>
                </div>
                <div>{parentDisplayName}</div>
              </div>
              <div className="existing-subscription-info">
                <div>
                  <strong>{formatMessage(i18nMessages.managedBy)}</strong>
                </div>
                <div>{parentDisplayName}</div>
              </div>
              <div className="existing-subscription-info">
                <div>
                  <strong>{formatMessage(i18nMessages.paidBy)}</strong>
                </div>
                <div>{displayNameOfSubscriptionBuyer}</div>
              </div>
              <br />
              <div className="existing-subscription-info">
                <div>
                  <strong>{formatMessage(i18nMessages.id)}</strong>
                </div>
                <div>{id}</div>
              </div>
              <div className="existing-subscription-info">
                <div>
                  <strong>{formatMessage(i18nMessages.noLimit)}</strong>
                </div>
                {shouldDisplayNoLimitData ? <div>{subscriptionText}</div> : <div>{DEFAULT_EMPTY_CONTENT}</div>}
              </div>
              <br />
              <div className="existing-subscription-info">
                <div>
                  <strong>{formatMessage(i18nMessages.status)}</strong>
                </div>
                <Pill type={accountStatus}>{statusText}</Pill>
              </div>
              <br />
              <div className="existing-subscription-info">
                <div>
                  <strong>{formatMessage(i18nMessages.startDate)}</strong>
                </div>
                <div>{purchasedOnText}</div>
              </div>
              <div className="existing-subscription-info">
                <div>
                  <strong>{formatMessage(i18nMessages.expirationRenewal)}</strong>
                </div>
                <div>{endsOnText}</div>
              </div>
              <br />
              <SubscriptionActionButtons
                redirectToChangeSubscriptionPage={() => {
                  history.push(
                    `/accounts/${managingAccountId}/change-subscription-for-client?clientId=${clientId}&showStoreCreditExplanation=true`,
                  );
                }}
                redirectToStopSubscriptionPage={() =>
                  history.push(`/accounts/${managingAccountId}/stop-subscription-for-client?clientId=${clientId}`)
                }
                managingAccountId={managingAccountId}
                clientAccountId={clientId}
                status={accountStatus}
                renewSubscription={renewSubscription}
                redirectToBuySubscriptionPage={() => {
                  history.push(`/accounts/${managingAccountId}/buy-subscription-for-client?clientId=${clientId}`);
                }}
              />
              <br />
              <div className="align-center">
                <Button
                  type="button"
                  label={formatMessage(i18nMessages.close)}
                  onClick={() => history.push(`/accounts/${managingAccountId}/manage-accounts`)}
                />
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagedAccountDetail;
